"use client";
import React, { useState, useEffect, useCallback } from "react";
import { ServiceModal } from "./serviceModal";
import { useRouter } from "next13-progressbar";
import { usePathname } from "next/navigation";
import { Menu } from "@/dto/menu";
import { GetMerchantPreferencesResponse } from "@/dataLayer/getMerchantPreferences";
import { Item } from "@/dto/item";
import { getItemId, itemIdRegex } from "@/utils/itemUrlUtils";
import { useItemRedirect } from "@/utils/useItemRedirect";
import { getMatchingModifiers } from "@/utils/modifierUtils";
import {
  ROUTE_NOT_FOUND,
  ROUTE_SERVICES,
  ROUTE_SERVICES_ALL,
} from "@/utils/constants";
import { focusItemCard } from "@/utils/focusItemCard";
import { ModalOverlay } from "@/components/modals/modalOverlay";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";

interface ServicesMenuInteractionsProps {
  merchantName: string;
  isCheckoutEnabled: boolean;
  menu: Menu | null;
  merchantId: string;
  isAcceptingAppointments: boolean;
  merchantPreferences: GetMerchantPreferencesResponse;
}

export const ServicesMenuInteractions = ({
  merchantName,
  isCheckoutEnabled,
  menu,
  merchantId,
  isAcceptingAppointments,
  merchantPreferences,
}: ServicesMenuInteractionsProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const [selectedService, setSelectedService] = useState<Item | null>(null);
  const [modifiers, setModifiers] = useState<ReturnType<
    typeof getMatchingModifiers
  > | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const merchantColor = useMerchantColor();

  const constructUrlAction = useCallback(
    (itemUrl: string) => `${ROUTE_SERVICES}/${itemUrl}`,
    []
  );

  useItemRedirect({
    ignorePath: ROUTE_SERVICES_ALL,
    constructUrlAction,
    items: menu?.items,
  });

  useEffect(() => {
    if (pathname !== ROUTE_SERVICES_ALL) {
      const serviceId = getItemId(pathname);
      if (serviceId) {
        if (!itemIdRegex.test(serviceId)) {
          router.replace(ROUTE_NOT_FOUND);
          return;
        }
        if (menu == null) return;
        const matchingService = menu.items.find(
          (item: any) => item.id === serviceId
        );
        if (matchingService) {
          setSelectedService(matchingService);
          setIsModalOpen(true);
          const matchedModifiers = getMatchingModifiers({
            modifiers: menu.modifiers,
            item: matchingService,
            modifierGroups: menu.modifierGroups,
          });
          setModifiers(matchedModifiers);
        } else {
          router.replace(ROUTE_NOT_FOUND);
          return;
        }
      } else {
        setIsModalOpen(false);
        setSelectedService(null);
        return;
      }
    }
  }, [pathname, menu?.items]);

  useEffect(() => {
    if (pathname === ROUTE_SERVICES_ALL) {
      focusItemCard();
    }
  }, [pathname]);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    setModifiers(null);
    router.replace(ROUTE_SERVICES_ALL, { scroll: false });
  };

  return (
    <>
      {isModalOpen && selectedService && (
        <>
          <ServiceModal
            isCheckoutEnabled={isCheckoutEnabled}
            merchantName={merchantName}
            merchantColor={merchantColor}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            service={selectedService}
            menu={menu}
            merchantId={merchantId}
            modifiers={modifiers}
            isAcceptingAppointments={isAcceptingAppointments}
            merchantPreferences={merchantPreferences}
          />
          <ModalOverlay />
        </>
      )}
    </>
  );
};
